// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if user is already authenticated
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      const result = await login(username, password);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Error al iniciar sesión. Por favor, intente nuevamente');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900">
      <div className="px-8 py-10 w-full max-w-md bg-gray-800 rounded-lg shadow-lg">
        {/* Logo THEND en lugar del ícono de usuario */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute h-px w-32 bg-gray-600 top-1/2 -left-36"></div>
            <div className="absolute h-px w-32 bg-gray-600 top-1/2 -right-36"></div>
            <div className="h-32 w-32 rounded-full border-2 border-gray-600 flex items-center justify-center overflow-hidden">
              <img 
                src="/images/thend-logo.png" 
                alt="Thend Online" 
                className="h-full w-full object-contain" 
                style={{ objectPosition: 'center' }}
              />
            </div>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
            <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <div className="flex overflow-hidden rounded-md">
              <div className="bg-gray-700 flex items-center justify-center px-4">
                <User className="text-gray-400" size={20} />
              </div>
              <input
                type="text"
                className="w-full p-3 bg-gray-700 border-l border-gray-600 text-white focus:outline-none"
                placeholder="USERNAME"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
          </div>
          
          <div className="mb-6">
            <div className="flex overflow-hidden rounded-md relative">
              <div className="bg-gray-700 flex items-center justify-center px-4">
                <Lock className="text-gray-400" size={20} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="w-full p-3 bg-gray-700 border-l border-gray-600 text-white focus:outline-none pr-10"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button 
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black focus:outline-none"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md transition-colors"
            disabled={loading}
          >
            {loading ? 'INICIANDO SESIÓN...' : 'LOGIN'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
